import type {ListItemBase} from "../../list/List";
import type {Categories} from "../../list/Category";
import type {FormPartOrReference} from "../FormPartOrReference";
import type {FormPart} from "../FormPart";
import type {LibraryFunctions} from "../Lib";
import {MaskOverride} from "./Masked";
import {AppContextMap} from "../AppContext";
import {UserInfo} from "../../user/UserInfo";
import {FormLayout} from "@t/fd/layout/FormLayout.ts";
import {FormEnvironment} from "@fd/Types.ts";
import {Flow} from "@t/fd/layout/Flow.ts";
import {Button} from "@t/fd/fixed/Button.ts";
import {AlwaysVisible} from "@t/fd/layout/AlwaysVisible.ts";
import {Empty} from "@t/fd/fixed/Empty.ts";
import {InterfaceRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";

export type FormList = ListItemBase[];
export interface FormLists {
    [key: string]: FormList;
}

export interface FormCategories {
    [key: string]: Categories;
}

export interface FormDefinitions {
    [key: string]: FormPartOrReference
}

export type FormMethodSource = string;
// fn(g, o, f, p, lib, ...extra)
export type FormMethodCompiled = (g:any, o:any, f:RootLayout, p:FormPart, r:InterfaceRoot<RootLayout>, lib:LibraryFunctions, ...extraArgs:any) => any;
export type FormMethod = FormMethodSource | FormMethodCompiled;

export interface FormMethods {
    [key: string]: FormMethod;
}

export interface DataAppContext {
    /** Name of context property */
    name: string;
    /** Data path to obtain property value */
    d: string;
}

export interface FormAppContext {
    fixed?: AppContextMap,
    data?: DataAppContext[]
}

export interface FormModifyData {
	[key: string]: any;
}

export interface FormDefinition extends FormLayout {
    type: "form";
    masks?: MaskOverride[];
	/**** Form content ****/
	body: FormPartOrReference;	// main body of the form (required) ... without any controls - just editing fields
	headerIcon?: string;				// Header information may not be used by client
	headerTitle?: string;				// It can provide its own or not display header at all
	header?: FormPartOrReference;		// Info-header of the form (if missing and headerIcon/headerTitle is provided then those are used to form the header)
	controls?: FormPartOrReference;		// Extra controls (other then standard save/add/create buttons)
	status?: FormPartOrReference;		// Optional component to display status information (entered, modified), client may not display this if space is limited

	/**** If the form is displayed as window/model, you can provide initial dimensions and resiability ****/
	initialWidth?: number;		// Recommended initial width of dialog window
	moveable?: boolean;			// Window can be moved around the screen
	resizable?: boolean;		// Window can be resized


	/**** Typical form is displayed like this (may differ based on device and other conditions):
		+--------------------------------------------------------+
		| (icon) (title) || (header)                         (x) |
		+--------------------------------------------------------+
		|                                                        |
		|                        (body)                          |
		|                                                        |
		+--------------------------------------------------------+
		| status  |  controls   |                        actions |
	    +--------------------------------------------------------+
	******/

	/**
	 * This object contains modifying data which can be picked-up by this form
	 * to alter its appearance.
	 */
	modifiers?: FormModifyData
}


export function dialogControls(env:FormEnvironment, actions:FormPartOrReference[] = ['save', 'saveAndBack']):FormPart {
	if (actions.length === 0) return <Empty>{ type: "empty" };
	let buttons:Flow = { type: "flow", items:[], options: { style: "flex-wrap: wrap-reverse" } };
	for (let action of actions) {
		if (typeof action === "string") {
			let part:FormPart = null;
			switch (action) {
				case "save":
					part = <Button>{ type: "button", mutating: true, label: env.msg('butt.save'), action: "save", feedback: true };
					break;
				case "saveAndBack":
					part = <Button>{ type: "button", mutating: true, label: env.msg('butt.saveAndBack'), action: "saveAndBack", feedback: true };
					break;
				case "add":
					part = <Button>{ type: "button", mutating: true, label: env.msg('bas.add'), action: "add", feedback: true };
					break;
				case "create":
					part = <Button>{ type: "button", mutating: true, label: env.msg('bas.create'), action: "create", feedback: true };
					break;
				case "ok":
					part = <Button>{ type: "button", mutating: true, label: env.msg('butt.ok'), action: "ok", feedback: true };
					break;
				case "update":
					part = <Button>{ type: "button", mutating: true, label: env.msg('butt.actualize'), action: "update", feedback: true };
					break;
				case "close":
					part = <Button>{ type: "button", label: env.msg('window.close'), action: "close", options: { clazz: "sbutton" } };
					break;
				case "cancel":
					part = <Button>{ type: "button", label: env.msg('bas.cancel'), action: "cancel", options: { clazz: "sbutton" } };
					break;
				case "back":
					part = <Button>{ type: "button", label: env.msg('bas.back'), action: "back", options: { clazz: "sbutton" } };
					break;
				case "continue":
					part = <Button>{ type: "button", label: env.msg('butt.continue'), action: "continue", feedback: true };
					break;
			}
			if (part) buttons.items.push(part);
		} else {
			buttons.items.push(action);
		}
	}
	let always:AlwaysVisible = <AlwaysVisible>{ type: "always-visible", body: buttons, options: { style: 'display: inline-block' } };
	return always;
}